import React, { useContext } from 'react';
import memoize from 'memoize-async';
import moment from 'moment';
import { ApLoaderDefault } from '@alixpartners/ui-components';
import DownloadProgressBar from 'Data/DownloadData/DownloadProgressBar';
import { flatten, groupBy } from 'lodash';

import { CHART_COLORS } from 'styles/colors';
import {
	createInitative,
	deleteInitative,
	getInitative,
	getInitatives,
	getInitativeCreators,
	getTotalSpend,
	getSpendCubeSettings,
	getSettingsOptions,
	getWaves,
	exportInitiatives,
} from 'utils/apiRequests';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { PermissionsContext } from 'contexts/PermissionsContext';
import { SettingsContext } from 'contexts/SettingsContext';
import { ApiDataContext } from 'contexts/ApiDataContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { getCurrencySymbolFromOptions, getHierarchyById } from 'Details/utils';
import { getRoundedPercentage, ITEMS_TYPE } from './utils';
import { SecondaryNav } from './SecondaryNav';
import InitiativeEditorModal from './InitiativeEditorModal';
import TopVendorsModal from 'Details/TopVendorsModal';
import InitiativesSummary from './InitiativesSummary';
import InitiativesList from './InitiativesList';
import { convertToArray } from 'Details/utils';
import isEqual from 'lodash/isEqual';

import {
	Wrapper,
	Content,
	LoadingContainer,
	NoDataMessage,
} from './Initiatives.styles';
import { SpendCubeSettingsContext } from 'contexts/SpendCubeSettingsContext';

const memoizedApi = {
	getTotalSpend: memoize(getTotalSpend),
	getSpendCubeSettings: memoize(getSpendCubeSettings),
	getSettingsOptions: memoize(getSettingsOptions),
	getWaves: memoize(getWaves),
	getInitatives: memoize(getInitatives),
	getInitativeCreators: memoize(getInitativeCreators),
};

export class Initiatives extends React.Component {
	state = {
		isEditorOpen: false,
		transformedInitiativesData: [],
		isLoading: true,
		editId: null,
		topVendorsModalData: null,
		hasExportError: false,
		isNotificationShown: false,
	};

	async componentDidMount() {
		await this.fetchData();
	}

	fetchData = async () => {
		await this.fetchSettings();
		await this.fetchTotalData();
		await this.fetchWaves();
		await this.fetchInitiativesData();
	};

	fetchSettings = async () => {
		const { apiCacheTrigger } = this.props;
		const settings = await memoizedApi.getSpendCubeSettings(apiCacheTrigger);
		const options = await memoizedApi.getSettingsOptions(apiCacheTrigger);

		const totalSpendDateRange = this.getDateRangeById(
			settings.initiativesTotalSpendDateRangeId,
			options.dateRangesForInitiativesTotalSpend
		);

		const initiativesDateRange = this.getDateRangeById(
			settings.initiativesDateRangeId,
			options.dateRangesForInitiatives
		);

		this.setState({
			totalSpendDateRange,
			initiativesDateRange,
		});
	};

	fetchWaves = async () => {
		const { apiCacheTrigger } = this.props;
		const initiativeWaves = await memoizedApi.getWaves(apiCacheTrigger);

		this.setState({
			initiativeWaves,
		});
	};

	getDateRangeById = (id, list) => {
		if (!list || list.length === 0) return null;

		if (!id) {
			return list[0];
		}

		const found = list.find(item => item.id === id);

		return found || list[0];
	};

	fetchTotalData = async () => {
		const { totalSpendDateRange } = this.state;
		const { currency, apiCacheTrigger } = this.props;
		const apiRequestParams = {};

		if (currency) {
			apiRequestParams.currency = currency;
			apiRequestParams.currencyType = 'fixed';
		}

		if (totalSpendDateRange) {
			apiRequestParams.dateFrom = totalSpendDateRange.dateFrom;
			apiRequestParams.dateTo = totalSpendDateRange.dateTo;
		}

		const totalValue = await memoizedApi.getTotalSpend(
			apiRequestParams,
			apiCacheTrigger
		);

		this.setState({ totalValue });
	};

	componentDidUpdate(prevProps, _prevState) {
		const {
			initiativeCreator,
			initiativeType,
			initiativeWave,
			taxonomy,
		} = this.props;
		const isPaymentTerms = this.getIsPaymentTerms();
		const wasPaymentTerms = prevProps.location.pathname.includes('/payment');
		const paymentTermsUpdated = wasPaymentTerms !== isPaymentTerms;

		if (
			!isEqual(prevProps.initiativeCreator, initiativeCreator) ||
			!isEqual(prevProps.initiativeType, initiativeType) ||
			!isEqual(prevProps.initiativeWave, initiativeWave) ||
			!isEqual(prevProps.taxonomy, taxonomy) ||
			paymentTermsUpdated
		) {
			const { originalInitiativesData, initiativesCreators } = this.state;

			this.setInitiativesData(originalInitiativesData, initiativesCreators);

			if (paymentTermsUpdated) {
				this.handleSecondaryNavFilterChange({ initiativeType: null });
			}
		}
	}

	getIsPaymentTerms() {
		const { location } = this.props;
		const isPaymentTerms = location.pathname.includes('/payment');

		return isPaymentTerms;
	}

	fetchInitiativesData = async () => {
		const { apiCacheTrigger } = this.props;

		const initiativesCreators = await memoizedApi.getInitativeCreators(
			{},
			apiCacheTrigger
		);
		let initiativesList = await memoizedApi.getInitatives({}, apiCacheTrigger);

		this.setInitiativesData(initiativesList, initiativesCreators);
	};

	setInitiativesData = (initiativesList, initiativesCreators) => {
		const {
			initiativeType,
			initiativeCreator,
			initiativeWave,
			taxonomy,
		} = this.props;
		const isPaymentTerms = this.getIsPaymentTerms();

		let filteredInitiativesList = initiativesList;

		if (isPaymentTerms) {
			filteredInitiativesList = filteredInitiativesList.filter(
				item =>
					item.initiativeItemType === ITEMS_TYPE.PT_HARMONIZATION ||
					item.initiativeItemType === ITEMS_TYPE.PT_IMPROVEMENT
			);
		} else {
			filteredInitiativesList = filteredInitiativesList.filter(
				item =>
					item.initiativeItemType === ITEMS_TYPE.CATEGORY ||
					item.initiativeItemType === ITEMS_TYPE.VENDOR ||
					item.initiativeItemType === ITEMS_TYPE.TAIL_SPEND
			);
		}

		if (initiativeType && initiativeType.length > 0) {
			filteredInitiativesList = filteredInitiativesList.filter(item =>
				initiativeType.includes(item.type)
			);
		}
		if (initiativeCreator && initiativeCreator.length > 0) {
			filteredInitiativesList = filteredInitiativesList.filter(item =>
				initiativeCreator.includes(item.createdBy)
			);
		}

		if (initiativeWave && initiativeWave.length > 0) {
			filteredInitiativesList = filteredInitiativesList.filter(item =>
				initiativeWave.includes(item.waveId && item.waveId.toString())
			);
		}

		if (taxonomy && taxonomy.length > 0) {
			const spendTypeTaxonomy = taxonomy.map(filter =>
				filter.split('_').map(item => Number(item))
			);

			filteredInitiativesList = filteredInitiativesList.filter(item =>
				spendTypeTaxonomy.some(
					taxonomyItem =>
						item.spendType === taxonomyItem[0] &&
						item.taxonomyType === taxonomyItem[1]
				)
			);
		}

		const initiativesListData = this.getTransformedInitiativesData(
			filteredInitiativesList
		);

		const initiativesSummaryData = this.getInitiativesSummaryData(
			filteredInitiativesList
		);

		this.setState({
			originalInitiativesData: initiativesList,
			initiativesCreators,
			initiativesListData,
			initiativesSummaryData,
			isLoading: false,
		});
	};

	refetchList = () => {
		const { clearApiCache } = this.props;

		clearApiCache();
		this.fetchInitiativesData();
	};

	getFormattedPrice = value => {
		const { currencySymbol, hideDecimals } = this.props;
		return formatSpendWithDecimalAndSymbol(
			value,
			currencySymbol,
			hideDecimals,
			2
		).toUpperCase();
	};

	getColorByCompanyName(name) {
		const { companiesList } = this.props;

		const index = companiesList.indexOf(name);

		return CHART_COLORS[index % CHART_COLORS.length];
	}

	getInitiativesSummaryData(initiativesList) {
		const { totalValue } = this.state;
		const isPaymentTerms = this.getIsPaymentTerms();

		let result = {
			total: '...',
			spend: 0,
			addressable: 0,
			workingCapitalImpact: 0,
			low: 0,
			high: 0,
			chartData: [],
			paymentTermsChartData: [],
		};

		const companiesMap = {};
		let chartTotal = 0;

		initiativesList.forEach(
			({ companies, addressable, low, high, spend, workingCapitalImpact }) => {
				result.workingCapitalImpact += workingCapitalImpact;
				result.addressable += addressable;
				result.spend += spend;
				result.low += low;
				result.high += high;

				companies.forEach(({ companyName: name, spend: chartValue }) => {
					chartTotal += chartValue;

					companiesMap[name] = companiesMap[name]
						? companiesMap[name] + chartValue
						: chartValue;
				});
			}
		);
		result.addressablePercentage = getRoundedPercentage(
			result.addressable / result.spend
		);
		result.lowPercentage = getRoundedPercentage(
			result.low / result.addressable
		);
		result.highPercentage = getRoundedPercentage(
			result.high / result.addressable
		);

		if (isPaymentTerms) {
			result.lowPercentage =
				Math.round(
					(initiativesList.reduce((acc, item) => acc + item.lowPercentage, 0) *
						100) /
						initiativesList.length
				) / 100;
			result.highPercentage =
				Math.round(
					(initiativesList.reduce((acc, item) => acc + item.highPercentage, 0) *
						100) /
						initiativesList.length
				) / 100;

			result.paymentTermsChartData = this.getPaymentTermsChartData(
				initiativesList
			);
		} else {
			result.chartData = Object.keys(companiesMap)
				.map(name => ({
					name,
					value: getRoundedPercentage(companiesMap[name] / chartTotal),
					color: this.getColorByCompanyName(name),
				}))
				.sort((a, b) => b.value - a.value);
		}

		const dateRangeSummaryData = this.getDateRangeForInitiativesSummary();

		result = {
			...result,
			...dateRangeSummaryData,
			totalValue: this.getFormattedPrice(totalValue),
			addressable: this.getFormattedPrice(result.addressable),
			workingCapitalImpact: this.getFormattedPrice(result.workingCapitalImpact),
			spend: this.getFormattedPrice(result.spend),
			low: this.getFormattedPrice(result.low),
			high: this.getFormattedPrice(result.high),
		};

		return result;
	}

	getPaymentTermsChartData = initiativesList => {
		const dpos = initiativesList.map(({ dpos }) => dpos);

		const grouppedOrigin = groupBy(
			flatten(dpos.reduce((acc, item) => [...acc, item.originDpos], [])),
			'dpo'
		);

		const grouppedHarmonized = groupBy(
			flatten(dpos.reduce((acc, item) => [...acc, item.harmonizedDpos], [])),
			'dpo'
		);

		const allDPOKeys = Object.keys({
			...grouppedOrigin,
			...grouppedHarmonized,
		}).sort();

		return allDPOKeys.map(key => {
			const origin = (grouppedOrigin[key] || []).reduce(
				(acc, item) => acc + item.spend,
				0
			);
			const harmonized = (grouppedHarmonized[key] || []).reduce(
				(acc, item) => acc + item.spend,
				0
			);
			return {
				label: key,
				values: [origin, harmonized],
			};
		});
	};

	getDateRangeForInitiativesSummary() {
		const { totalSpendDateRange, initiativesDateRange } = this.state;
		const dateFormat = 'MMM YYYY';

		return {
			totalSpendDateFrom: totalSpendDateRange
				? moment(totalSpendDateRange.dateFrom).format(dateFormat)
				: null,
			totalSpendDateTo: totalSpendDateRange
				? moment(totalSpendDateRange.dateTo).format(dateFormat)
				: null,
			initiativeSpendDateFrom: initiativesDateRange
				? moment(initiativesDateRange.dateFrom).format(dateFormat)
				: null,
			initiativeSpendDateTo: initiativesDateRange
				? moment(initiativesDateRange.dateTo).format(dateFormat)
				: null,
		};
	}

	getTransformedInitiativesData(initiativesList) {
		return initiativesList.map(initiative => {
			const { companies, createdDate, addressable, spend, low } = initiative;
			const date = moment(createdDate).format('MMM Do, YYYY');
			const chartData = companies
				.map(({ companyName: name, spend: value }) => ({
					name,
					value,
					color: this.getColorByCompanyName(name),
				}))
				.sort((a, b) => b.value - a.value);

			const result = {
				...initiative,
				addressable: this.getFormattedPrice(addressable),
				spend: this.getFormattedPrice(spend),
				low: this.getFormattedPrice(low),
				// high: this.getFormattedPrice(high),
				chartData,
				date,
			};

			return result;
		});
	}

	showEditor = editId => {
		this.setState({ isEditorOpen: true, editId });
	};

	hideEditor = () => {
		this.setState({ isEditorOpen: false, editId: null });
	};

	handleDeleteInitiative = async id => {
		const { clearApiCache } = this.props;
		this.setState({ isUpdating: true });
		clearApiCache();

		await deleteInitative(id);

		this.fetchInitiativesData();
		this.setState({ isUpdating: false });
	};

	handleDuplicateInitiative = async id => {
		const { clearApiCache } = this.props;
		this.setState({ isUpdating: true });
		clearApiCache();

		const initiative = await getInitative(id);
		const newInitiative = {
			...initiative,
			name: this.getDuplicatedInitiativeName(initiative.name),
		};

		delete newInitiative.id;
		delete newInitiative.initiativeId;

		await createInitative(newInitiative);

		this.fetchInitiativesData();
		this.setState({ isUpdating: false });
	};

	getDuplicatedInitiativeName = originalName => {
		const { initiativesListData } = this.state;
		let count = 1;
		let newName = '';
		const findCb = ({ name }) => name === newName;

		do {
			newName =
				count === 1 ? `${originalName} Copy` : `${originalName} Copy ${count}`;
			count += 1;
		} while (initiativesListData.find(findCb));

		return newName;
	};

	handleSecondaryNavFilterChange = queryState => {
		const { setQueryStringStateAndPush } = this.props;

		setQueryStringStateAndPush(queryState);
	};

	handleResetFilters = () => {
		const { setQueryStringStateAndPush } = this.props;

		setQueryStringStateAndPush({
			initiativeType: null,
			initiativeWave: null,
			initiativeCreator: null,
		});
	};

	handleInitiativeCreate = () => {
		this.showEditor();
	};

	showTopVendorsModal = (initiativeData, item) => {
		const params = {
			...initiativeData,
			...(item ? { hierarchy: getHierarchyById(item.id) } : {}),
		};

		this.setState({
			topVendorsModalData: {
				initiativeParams: params,
				name: item ? item.name : initiativeData.name,
			},
		});
	};

	hideTopVendorsModal = () => {
		this.setState({
			topVendorsModalData: null,
		});
	};

	closeNotification = () => {
		this.setState({ isNotificationShown: false, hasExportError: false });
	};

	downloadFailed = () => {
		this.setState({ hasExportError: true });
	};

	clearDownloadError = () => {
		this.setState({ hasExportError: false });
	};

	showDownloadNotification = () => {
		this.setState({ isNotificationShown: true });

		setTimeout(() => {
			this.closeNotification();
		}, 5000);
	};

	handleExport = () => {
		this.clearDownloadError();

		exportInitiatives()
			.catch(this.downloadFailed)
			.finally(this.showDownloadNotification);
	};

	render() {
		const {
			isEditorOpen,
			initiativesListData,
			initiativesSummaryData,
			initiativesCreators,
			initiativeWaves,
			isLoading,
			isUpdating,
			editId,
			originalInitiativesData,
			topVendorsModalData,
			isNotificationShown,
			hasExportError,
		} = this.state;
		const {
			currencySymbol,
			initiativeType,
			initiativeWave,
			initiativeCreator,
			taxonomy,
			isParent,
			isPortco,
			hideDecimals,
		} = this.props;

		if (isLoading) {
			return (
				<Wrapper>
					<LoadingContainer>
						<ApLoaderDefault />
					</LoadingContainer>
				</Wrapper>
			);
		}

		const isPaymentTerms = this.getIsPaymentTerms();

		const anyInitiativesExist = originalInitiativesData.length > 0;
		const hasFiltersApplied =
			(initiativeType && initiativeType.length > 0) ||
			(initiativeWave && initiativeWave.length > 0) ||
			(initiativeCreator && initiativeCreator.length > 0);

		const hasInitiativeData =
			initiativesListData && initiativesListData.length > 0;

		return (
			<Wrapper isUpdating={isUpdating}>
				{anyInitiativesExist && (
					<SecondaryNav
						onCreate={this.handleInitiativeCreate}
						creators={initiativesCreators}
						waves={initiativeWaves}
						type={initiativeType}
						wave={initiativeWave}
						creator={initiativeCreator}
						taxonomy={taxonomy}
						onFilterChange={this.handleSecondaryNavFilterChange}
						isPaymentTerms={isPaymentTerms}
						onExport={this.handleExport}
						isParent={isParent}
						isPortco={isPortco}
					/>
				)}
				{!hasInitiativeData ? (
					<NoDataMessage
						onCreate={this.handleInitiativeCreate}
						onReset={this.handleResetFilters}
						isReset={hasFiltersApplied}
					/>
				) : (
					<React.Fragment>
						<Content>
							<InitiativesSummary
								data={initiativesSummaryData}
								currencySymbol={currencySymbol}
								isPaymentTerms={isPaymentTerms}
							/>

							<InitiativesList
								data={initiativesListData}
								currencySymbol={currencySymbol}
								onEdit={id => {
									this.showEditor(id);
								}}
								onDelete={this.handleDeleteInitiative}
								onDuplicate={this.handleDuplicateInitiative}
								isPaymentTerms={isPaymentTerms}
								showTopVendors={this.showTopVendorsModal}
							/>
						</Content>
					</React.Fragment>
				)}
				{isEditorOpen && (
					<InitiativeEditorModal
						onClose={this.hideEditor}
						refetchList={this.refetchList}
						id={editId}
						showTopVendors={this.showTopVendorsModal}
						hideDecimals={hideDecimals}
					/>
				)}
				{topVendorsModalData && (
					<TopVendorsModal
						onClose={this.hideTopVendorsModal}
						{...topVendorsModalData}
					/>
				)}
				{isNotificationShown && (
					<DownloadProgressBar
						error={hasExportError}
						successMessage="Generating file... You will receive a notification when the file has been generated"
						onClose={this.closeNotification}
					/>
				)}
			</Wrapper>
		);
	}
}

export default React.memo(props => {
	const {
		currency,
		showMappedItems,
		setQueryStringStateAndPush,
		initiativeType,
		initiativeWave,
		initiativeCreator,
		taxonomy,
	} = useContext(QueryStringContext);
	const { permissions } = useContext(PermissionsContext);
	const { companiesList, hideDecimals } = useContext(SettingsContext);
	const { clearApiCache, apiCacheTrigger } = useContext(ApiDataContext);
	const {
		options: { currency: currencyList },
	} = useContext(SettingsOptionsContext);
	const { settings: spendCubeSettings } = useContext(SpendCubeSettingsContext);
	const currencySymbol = getCurrencySymbolFromOptions(currencyList, currency);
	return (
		<Initiatives
			{...props}
			permissions={permissions || []}
			showMappedItems={!!showMappedItems}
			setQueryStringStateAndPush={setQueryStringStateAndPush}
			currencySymbol={currencySymbol}
			currency={currency}
			companiesList={companiesList}
			clearApiCache={clearApiCache}
			apiCacheTrigger={apiCacheTrigger}
			taxonomy={convertToArray(taxonomy)}
			initiativeType={convertToArray(initiativeType)}
			initiativeWave={convertToArray(initiativeWave)}
			initiativeCreator={convertToArray(initiativeCreator)}
			isParent={spendCubeSettings.isParent}
			isPortco={spendCubeSettings.isPortco}
			hideDecimals={hideDecimals}
		/>
	);
});
